/**
 * RecsPanel Component
 * Smart slot recommendations based on active slot criteria
 */

import { Sparkles, AlertCircle, TrendingUp, Music, Loader2, GripVertical } from 'lucide-react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useWorkbenchStore, type SmartSlot } from '../../stores/workbench';
import type { TrackMatch } from '../../api/services/smart-slots';
import { smartSlotService } from '../../api/services/smart-slots';
import { getScoreColor } from '../../lib/smart-slot-matcher';
import { SourceSelector, type MatchSource } from './SourceSelector';
import { useTrackMetadataStore } from '../../stores/trackMetadata';
import type { Track } from '../../api/types';
import { useState, useEffect } from 'react';

/**
 * Format criteria for display
 */
function formatCriteria(slot: SmartSlot): string {
  const parts: string[] = [];

  if (slot.criteria.bpm) {
    parts.push(`${slot.criteria.bpm.min}-${slot.criteria.bpm.max} BPM`);
  }

  if (slot.criteria.keys && slot.criteria.keys.length > 0) {
    const keyDisplay = slot.criteria.keys.length > 3
      ? `${slot.criteria.keys.slice(0, 3).join(', ')}+${slot.criteria.keys.length - 3}`
      : slot.criteria.keys.join(', ');
    parts.push(`Keys: ${keyDisplay}`);
  }

  if (slot.criteria.energy) {
    parts.push(`Energy: ${(slot.criteria.energy.min * 100).toFixed(0)}-${(slot.criteria.energy.max * 100).toFixed(0)}%`);
  }

  if (slot.criteria.valence) {
    parts.push(`Valence: ${(slot.criteria.valence.min * 100).toFixed(0)}-${(slot.criteria.valence.max * 100).toFixed(0)}%`);
  }

  if (slot.criteria.danceability) {
    parts.push(`Dance: ${(slot.criteria.danceability.min * 100).toFixed(0)}-${(slot.criteria.danceability.max * 100).toFixed(0)}%`);
  }

  return parts.join(' • ');
}

/**
 * Match Card Component
 * Uses local track data from workbench store since backend doesn't return track metadata
 */
function MatchCard({
  match,
  track,
  trackName,
  trackArtist,
  trackAlbum,
  albumArt,
  onSelect
}: {
  match: TrackMatch;
  track: Track;
  trackName: string;
  trackArtist: string;
  trackAlbum: string | null;
  albumArt: string | null;
  onSelect: () => void;
}) {
  const scoreColor = getScoreColor(match.score);

  // Make track draggable
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `recs-${track.id}`,
    data: {
      type: 'external-track',
      track,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all group ${
        isDragging ? 'cursor-grabbing' : ''
      }`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="float-right cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity ml-2"
        title="Drag to workbench"
      >
        <GripVertical className="w-4 h-4 text-zinc-500" />
      </div>

      <button
        onClick={onSelect}
        className="w-full text-left"
      >
      <div className="flex gap-3">
        {/* Album Art */}
        <div className="relative w-12 h-12 rounded-md overflow-hidden bg-zinc-800 flex-shrink-0">
          {albumArt ? (
            <img src={albumArt} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-5 h-5 text-zinc-600" />
            </div>
          )}
          {/* Score Badge */}
          <div className={`absolute top-0 right-0 px-1.5 py-0.5 text-[10px] font-bold ${scoreColor} bg-black/60 rounded-bl`}>
            {match.score}
          </div>
        </div>

        {/* Track Info */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-100 truncate group-hover:text-emerald-400 transition-colors">
            {trackName || 'Unknown Track'}
          </div>
          <div className="text-xs text-zinc-500 truncate">
            {trackArtist || 'Unknown Artist'}
          </div>

          {/* Album */}
          {trackAlbum && (
            <div className="text-[10px] text-zinc-600 truncate mt-0.5">
              {trackAlbum}
            </div>
          )}
        </div>

        {/* Match Score */}
        <div className="flex flex-col items-end justify-center gap-1">
          <div className={`text-lg font-bold ${scoreColor}`}>
            {match.score}
          </div>
          <div className="text-[9px] text-zinc-600 uppercase tracking-wide">
            Match
          </div>
        </div>
      </div>

      {/* Score Breakdown - These are match scores (0-100), not actual feature values */}
      <div className="mt-2 pt-2 border-t border-zinc-800 flex items-center gap-3 text-[10px]">
        <div className="flex items-center gap-1">
          <span className="text-zinc-600">BPM Match:</span>
          <span className="text-zinc-400 font-medium">{match.breakdown.bpm.toFixed(0)}/100</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-zinc-600">Key:</span>
          <span className="text-zinc-400 font-medium">{match.breakdown.key.toFixed(0)}/100</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-zinc-600">Energy:</span>
          <span className="text-zinc-400 font-medium">{match.breakdown.energy.toFixed(0)}/100</span>
        </div>
      </div>
      </button>
    </div>
  );
}

export function RecsPanel() {
  const { activeSlotId, smartSlots, tracks, addTrack, updateSmartSlot, removeTrack } = useWorkbenchStore();
  const { getTrack: getCachedTrack } = useTrackMetadataStore();

  // Find active slot
  const activeSlot = smartSlots.find((s) => s.id === activeSlotId);

  // Independent search state
  const [source, setSource] = useState<MatchSource>({ type: 'workbench' });
  const [matches, setMatches] = useState<TrackMatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch matches when active slot or source changes
  useEffect(() => {
    if (!activeSlot || !activeSlot.criteria) {
      setMatches([]);
      return;
    }

    const fetchMatches = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Use the new smartSlotService API that accepts MatchSource
        const response = await smartSlotService.findMatches(
          activeSlot.criteria,
          source,
          { limit: 50, minScore: 40 }
        );

        setMatches(response.matches);
      } catch (err) {
        console.error('Failed to fetch matches:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch matches');
        setMatches([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMatches();
  }, [activeSlot?.id, activeSlot?.criteria, source]);

  // Handle filling slot with selected track
  const handleSelectMatch = (match: TrackMatch) => {
    if (!activeSlot) return;

    // Try to find track in workbench first
    let trackToAdd = tracks.find((t) => t.id === match.trackId);

    // Check if track already exists in workbench (for Juggle functionality)
    const originalIndex = tracks.findIndex(t => t.id === match.trackId);
    const isAlreadyInWorkbench = originalIndex >= 0 && originalIndex !== activeSlot.position;

    // If not in workbench, try the metadata cache
    if (!trackToAdd) {
      const cachedMetadata = getCachedTrack(match.trackId);
      if (cachedMetadata) {
        // Convert cached metadata to Track format
        trackToAdd = {
          id: cachedMetadata.id,
          uri: cachedMetadata.uri,
          name: cachedMetadata.name,
          artists: cachedMetadata.artists,
          album: cachedMetadata.album,
          duration_ms: 0, // Not cached, but not needed for display
          explicit: false,
          popularity: 0,
        } as any; // Type assertion since we don't have all Track fields
      }
    }

    if (!trackToAdd) {
      console.error('Track not found in workbench or cache:', match.trackId);
      return;
    }

    // Calculate final position accounting for removal if needed
    let finalPosition = activeSlot.position;

    // JUGGLE: Remove from original position FIRST if it was already in workbench
    // This prevents duplicates since removeTrack filters by ID
    if (isAlreadyInWorkbench) {
      // If removing a track before the slot position, adjust position down
      if (originalIndex < activeSlot.position) {
        finalPosition = activeSlot.position - 1;
      }
      removeTrack(match.trackId);
    }

    // IMPORTANT: Mark slot as filled FIRST before adding track
    // This prevents addTrack from shifting the slot's position
    updateSmartSlot(activeSlot.id, {
      filled: true,
      suggestedTrackId: match.trackId,
    });

    // Add track at calculated position
    addTrack(trackToAdd, finalPosition);
  };

  // No active slot
  if (!activeSlot) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 py-12 text-center">
        <AlertCircle className="w-12 h-12 text-zinc-700 mb-3" />
        <p className="text-sm text-zinc-400 mb-2">No active smart slot</p>
        <p className="text-xs text-zinc-500">
          Create a smart slot in the workbench to see matching recommendations
        </p>
      </div>
    );
  }

  const criteriaText = formatCriteria(activeSlot);

  return (
    <div className="flex flex-col h-full">
      {/* Header with Criteria Summary */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <Sparkles className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-zinc-100 mb-1">
              Matches for Slot {activeSlot.position + 1}
            </h3>
            <p className="text-xs text-zinc-400 break-words">
              {criteriaText || 'No criteria specified'}
            </p>
          </div>
        </div>
      </div>

      {/* Source Selector */}
      <SourceSelector
        source={source}
        onSourceChange={setSource}
        isSearching={isLoading}
      />

      {/* Recommendations List */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Loader2 className="w-12 h-12 text-emerald-500 mb-3 animate-spin" />
            <p className="text-sm text-zinc-400">Finding matches...</p>
            <p className="text-xs text-zinc-600 mt-1">
              Searching{' '}
              {source.type === 'workbench'
                ? 'current workbench'
                : source.type === 'playlists'
                ? `${source.playlistIds.length} playlist${source.playlistIds.length === 1 ? '' : 's'}`
                : source.type === 'playlist'
                ? 'playlist'
                : source.type === 'liked'
                ? 'Liked Songs'
                : 'all playlists'}
              ...
            </p>
          </div>
        ) : error ? (
          /* Error State */
          <div className="flex flex-col items-center justify-center h-full text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
            <p className="text-sm text-red-400 mb-2">Failed to fetch matches</p>
            <p className="text-xs text-zinc-500 max-w-xs">{error}</p>
          </div>
        ) : matches.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <TrendingUp className="w-12 h-12 text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-400 mb-2">No matches found</p>
            <p className="text-xs text-zinc-500 max-w-xs">
              Try adjusting your criteria or ensure tracks have audio features analyzed.
              In the future, this will search your full library and catalogs.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500 mb-3">
              Found {matches.length} matching tracks
            </div>
            {matches.map((match, index) => {
              // Look up track data from workbench store first, then metadata cache
              const localTrack = tracks.find((t) => t.id === match.trackId);
              const cachedTrack = localTrack ? null : getCachedTrack(match.trackId);

              // Use local track if available, otherwise construct from cached metadata
              const track: Track = localTrack || (cachedTrack ? {
                id: cachedTrack.id,
                uri: cachedTrack.uri,
                name: cachedTrack.name,
                artists: cachedTrack.artists,
                album: cachedTrack.album,
                durationMs: 0,
                duration_ms: 0,
                explicit: false,
                popularity: 0,
              } as Track : {
                id: match.trackId,
                uri: `spotify:track:${match.trackId}`,
                name: match.name || 'Unknown Track',
                artists: [{ name: match.artist || 'Unknown Artist', id: '', uri: '' }],
                album: {
                  name: match.album || '',
                  images: [],
                  id: '',
                  uri: '',
                  releaseDate: '',
                  artists: [],
                },
                durationMs: 0,
                duration_ms: 0,
                explicit: false,
                popularity: 0,
              } as Track);

              const trackName = track.name;
              const trackArtist = track.artists.map(a => a.name).join(', ');
              const trackAlbum = track.album.name;
              const albumArt = track.album.images?.[0]?.url || null;

              return (
                <MatchCard
                  key={match.trackId + index}
                  match={match}
                  track={track}
                  trackName={trackName}
                  trackArtist={trackArtist}
                  trackAlbum={trackAlbum}
                  albumArt={albumArt}
                  onSelect={() => handleSelectMatch(match)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
